import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  MdAdd, MdClose, MdMoreVert, MdEdit, MdArchive, MdUnarchive,
  MdCardMembership, MdCalendarMonth,
} from 'react-icons/md';
import * as planSvc from '../../services/membershipPlanService';
import useTheme from '../../context/ThemeContext';
import useImpersonation from '../../hooks/useImpersonation';

const fmtNPR = (n) => `NPR ${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const perMonth = (price, months) => (months > 0 ? Math.round(Number(price) / months) : 0);

// ── Add / Edit modal — scrollable body, sticky footer, mobile sheet ──────────────
function PlanModal({ plan, initial, onClose, onSaved }) {
  const isEdit = !!plan?._id;
  const currentTheme = useTheme()?.currentTheme;
  const primaryBtn = currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card';

  const [form, setForm] = useState({
    name:           plan?.name ?? initial?.name ?? '',
    durationMonths: plan?.durationMonths ?? initial?.durationMonths ?? '',
    price:          plan?.price ?? '',
    description:    plan?.description ?? '',
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const months = Number(form.durationMonths);
    if (!form.name.trim()) return toast.error('Name is required');
    if (!Number.isInteger(months) || months < 1 || months > 24) return toast.error('Duration must be 1–24 months');
    if (!(Number(form.price) >= 0)) return toast.error('Price must be 0 or greater');

    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        durationMonths: months,
        price: Number(form.price),
        description: form.description.trim() || undefined,
      };
      if (isEdit) { await planSvc.updateMembershipPlan(plan._id, payload); toast.success('Plan updated'); }
      else { await planSvc.createMembershipPlan(payload); toast.success('Plan added'); }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save plan');
    } finally {
      setSubmitting(false);
    }
  };

  const inp = 'w-full px-3 py-2.5 rounded-lg border text-sm outline-none transition-colors bg-nav-hover/40 border-divider focus:border-primary focus:ring-2 focus:ring-primary/30 text-secondary placeholder:text-subtle';
  const lbl = 'block text-xs font-semibold text-muted mb-1.5 uppercase tracking-wide';
  const months = Number(form.durationMonths);
  const showPerMonth = months >= 1 && Number(form.price) > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-card border border-divider rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-divider flex-shrink-0">
          <h2 className="font-bold text-secondary flex items-center gap-2">
            <MdCardMembership className="text-primary" /> {isEdit ? 'Edit Plan' : 'Add Plan'}
          </h2>
          <button onClick={onClose} className="w-11 h-11 -mr-2 flex items-center justify-center rounded-lg text-muted hover:bg-nav-hover transition-colors">
            <MdClose className="text-lg" />
          </button>
        </div>

        <form onSubmit={submit} className="flex flex-col min-h-0 flex-1">
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
            <div>
              <label className={lbl}>Plan Name <span className="text-red-500">*</span></label>
              <input className={inp} value={form.name} onChange={e => set('name', e.target.value)}
                placeholder="e.g. 3 Months" maxLength={60} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl}>Duration (months) <span className="text-red-500">*</span></label>
                <input className={inp} type="number" min="1" max="24" step="1" value={form.durationMonths}
                  onChange={e => set('durationMonths', e.target.value)} placeholder="3" required />
              </div>
              <div>
                <label className={lbl}>Price (NPR) <span className="text-red-500">*</span></label>
                <input className={inp} type="number" min="0" step="1" value={form.price}
                  onChange={e => set('price', e.target.value)} placeholder="8000" required />
              </div>
            </div>
            {showPerMonth && (
              <p className="text-[12px] text-muted -mt-1">Works out to {fmtNPR(perMonth(form.price, months))}/month.</p>
            )}
            <div>
              <label className={lbl}>Description <span className="text-subtle normal-case font-normal">(optional)</span></label>
              <textarea className={inp} rows={2} value={form.description} onChange={e => set('description', e.target.value)}
                maxLength={200} placeholder="What's included, any notes members should know…" />
            </div>
          </div>

          <div className="flex justify-end gap-2 px-5 py-3 border-t border-divider flex-shrink-0">
            <button type="button" onClick={onClose}
              className="px-4 py-2.5 rounded-full border border-divider text-secondary text-sm font-medium hover:bg-nav-hover transition-colors min-h-[44px]">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold ${primaryBtn} hover:opacity-90 active:scale-[0.98] transition-all duration-150 disabled:opacity-50 min-h-[44px]`}>
              {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── One plan card ────────────────────────────────────────────────────────────────
function PlanCard({ plan, canEdit, onEdit, onArchive }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const archived = !plan.isActive;
  return (
    <div className={`relative rounded-2xl border p-5 transition-colors ${archived ? 'border-divider bg-nav-hover/20' : 'border-divider bg-card'}`}>
      {/* Overflow menu */}
      {canEdit && (
        <div className="absolute top-3 right-3">
          <button onClick={() => setMenuOpen(o => !o)}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-muted hover:bg-nav-hover hover:text-secondary transition-colors"
            title="Options">
            <MdMoreVert className="text-lg" />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 mt-1 w-40 bg-card border border-divider rounded-xl shadow-lg z-20 py-1">
                {!archived && (
                  <button onClick={() => { setMenuOpen(false); onEdit(); }}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-secondary hover:bg-nav-hover transition-colors">
                    <MdEdit className="text-base text-muted" /> Edit
                  </button>
                )}
                <button onClick={() => { setMenuOpen(false); onArchive(); }}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-secondary hover:bg-nav-hover transition-colors">
                  {archived ? <><MdUnarchive className="text-base text-muted" /> Restore</> : <><MdArchive className="text-base text-muted" /> Archive</>}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <p className={`font-bold text-base pr-8 ${archived ? 'text-muted' : 'text-secondary'}`}>{plan.name}</p>
      <div className="flex items-center gap-1.5 mt-1 text-[12px] text-muted">
        <MdCalendarMonth className="text-[14px]" />
        {plan.durationMonths} month{plan.durationMonths === 1 ? '' : 's'}
      </div>

      <p className={`text-2xl font-bold mt-3 ${archived ? 'text-muted' : 'text-secondary'}`}>{fmtNPR(plan.price)}</p>
      {plan.price > 0 && (
        <p className="text-[12px] text-muted mt-0.5">{fmtNPR(perMonth(plan.price, plan.durationMonths))}/month</p>
      )}

      {plan.description && <p className="text-[13px] text-muted mt-3 leading-snug">{plan.description}</p>}

      {archived && (
        <span className="inline-block mt-3 text-[11px] font-semibold text-subtle bg-nav-hover px-2 py-0.5 rounded-full">Archived</span>
      )}
    </div>
  );
}

// ── Section ──────────────────────────────────────────────────────────────────────
export default function MembershipPlansSection() {
  const qc = useQueryClient();
  const currentTheme = useTheme()?.currentTheme;
  const { isReadOnly } = useImpersonation();
  const canEdit = !isReadOnly;

  const [modal, setModal] = useState(null);     // { plan } | { initial } | { } — open state
  const [showArchived, setShowArchived] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ['membership-plans'],
    queryFn: () => planSvc.getMembershipPlans().then(r => r.data),
  });
  const plans = data?.plans || [];
  const active   = plans.filter(p => p.isActive);
  const archived = plans.filter(p => !p.isActive);

  const archiveMutation = useMutation({
    mutationFn: (id) => planSvc.toggleArchiveMembershipPlan(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['membership-plans'] }),
    onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const afterSave = () => { qc.invalidateQueries({ queryKey: ['membership-plans'] }); setModal(null); };

  const onArchive = (plan) => {
    const verb = plan.isActive ? 'Archive' : 'Restore';
    if (window.confirm(`${verb} the plan “${plan.name}”?${plan.isActive ? ' It will be hidden from the plan list but kept for your records.' : ''}`)) {
      archiveMutation.mutate(plan._id);
    }
  };

  const primaryBtn = currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card';

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div>
          <h2 className="text-base font-bold text-secondary flex items-center gap-2">
            <MdCardMembership className="text-primary" /> Membership Plans
          </h2>
          <p className="text-xs text-muted mt-0.5">Set the packages members can buy — you decide the price for each duration.</p>
        </div>
        {canEdit && active.length + archived.length > 0 && (
          <button onClick={() => setModal({})}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold ${primaryBtn} hover:opacity-90 active:scale-[0.98] transition-all duration-150 min-h-[44px] flex-shrink-0`}>
            <MdAdd className="text-base" /> Add Plan
          </button>
        )}
      </div>

      {isPending ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2].map(i => <div key={i} className="h-40 bg-nav-hover rounded-2xl animate-pulse" />)}
        </div>
      ) : active.length === 0 && archived.length === 0 ? (
        /* Empty state */
        <div className="bg-card border border-divider rounded-2xl p-8 text-center">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
            <MdCardMembership className="text-primary text-2xl" />
          </div>
          <p className="font-bold text-secondary">No plans yet</p>
          <p className="text-sm text-muted mt-1 max-w-md mx-auto">
            Most gyms offer a few durations — 1 Month, 3 Months, 6 Months and 1 Year. Add your first plan and set its price.
          </p>
          {canEdit && (
            <div className="flex items-center justify-center gap-2 mt-5 flex-wrap">
              <button onClick={() => setModal({ initial: { name: '1 Month', durationMonths: 1 } })}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold ${primaryBtn} hover:opacity-90 active:scale-[0.98] transition-all duration-150 min-h-[44px]`}>
                <MdAdd className="text-base" /> Add your first plan
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Active plans */}
          {active.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {active.map(p => (
                <PlanCard key={p._id} plan={p} canEdit={canEdit}
                  onEdit={() => setModal({ plan: p })} onArchive={() => onArchive(p)} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted">No active plans. {archived.length > 0 && 'Restore one below or add a new plan.'}</p>
          )}

          {/* Archived */}
          {archived.length > 0 && (
            <div className="mt-6">
              <button onClick={() => setShowArchived(s => !s)}
                className="text-xs font-semibold text-muted hover:text-secondary transition-colors flex items-center gap-1.5">
                {showArchived ? '▾' : '▸'} Archived ({archived.length})
              </button>
              {showArchived && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
                  {archived.map(p => (
                    <PlanCard key={p._id} plan={p} canEdit={canEdit}
                      onEdit={() => setModal({ plan: p })} onArchive={() => onArchive(p)} />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {modal && (
        <PlanModal
          plan={modal.plan}
          initial={modal.initial}
          onClose={() => setModal(null)}
          onSaved={afterSave}
        />
      )}
    </div>
  );
}
